import {
  Button,
  EHeadingStyleVariant,
  ETextInputTypes,
  EToastVariant,
  Heading,
  TextInput,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import zxcvbn from 'zxcvbn';

import { useToast } from '@/hooks/useToast';
import { updateUser } from '@/redux/modules/auth';
import { TAppDispatch } from '@/redux/rootReducer';

const containsNumberLowercaseUppercase = (password: string) => {
  const hasNumber = /\d/;
  const hasLowercase = /[a-z]/;
  const hasUppercase = /[A-Z]/;

  return hasNumber.test(password) && hasLowercase.test(password) && hasUppercase.test(password);
};

interface IPasswordChangeProps {
  refreshUser: () => void;
}

export const PasswordChange = ({ refreshUser }: IPasswordChangeProps) => {
  const intl = useIntl();
  const dispatch: TAppDispatch = useDispatch();
  const isGreaterThanTablet = useIsWindowWidthGreaterThan('md', true);

  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');

  const [isUserUpdating, setIsUserUpdating] = useState(false);
  const [score, setScore] = useState(0);

  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmedPasswordError, setConfirmedPasswordError] = useState('');

  if (!zxcvbn) {
    return null;
  }

  const currentPasswordLabel = intl.formatMessage({
    defaultMessage: 'Current password',
    id: 'NIynnB',
  });
  const newPasswordLabel = intl.formatMessage({
    defaultMessage: 'New password',
    id: 'fTHhSB',
  });
  const confirmNewPasswordLabel = intl.formatMessage({
    defaultMessage: 'Confirm new password',
    id: 'fe7Y2Z',
  });
  const showPasswordLabel = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'JHTwdB',
  });
  const changePasswordLabel = intl.formatMessage({
    defaultMessage: 'Change password',
    id: 'L4nXIc',
  });
  const weakPassword = intl.formatMessage({
    defaultMessage:
      "Your password is still too weak. To strengthen it, try making it longer, incorporating special characters, and avoiding repeated characters (e.g., 'aaa') and sequences (e.g., 'abcd').",
    id: 'fb40eh',
  });
  const mustMatch = intl.formatMessage({
    defaultMessage: 'Both passwords must match.',
    id: '2gqLkb',
  });
  const requiredField = intl.formatMessage({
    defaultMessage: 'This field is required.',
    id: '6B5Jtu',
  });
  const successToastTitle = intl.formatMessage({
    defaultMessage: 'Success',
    id: 'xrKHS6',
  });
  const successToastDescription = intl.formatMessage({
    defaultMessage: 'Your password has been changed.',
    id: 'kgHPAz',
  });

  const onCurrentPasswordChangeHandler = (value: string) => {
    if (currentPassword.length === 0 && value.length > 0) {
      setCurrentPasswordError('');
    }

    setCurrentPassword(value);
  };

  const onNewPasswordChangeHandler = (value: string) => {
    setNewPassword(value);

    // If result is >= 3 the BE should not return an error.
    // The min length and the required chars are here to insure that but it still possible the entered password to be weak.
    const result = zxcvbn(value, [currentPassword]).score;
    setScore(result);

    if (value.length < 8) {
      setNewPasswordError(
        intl.formatMessage({
          defaultMessage: 'Password must be at least eight characters long.',
          id: 'zjiTOE',
        }),
      );
    } else if (!containsNumberLowercaseUppercase(value)) {
      setNewPasswordError(
        intl.formatMessage({
          defaultMessage:
            'Password must include at least one uppercase letter, one lowercase letter, and one number.',
          id: 'WGvnmN',
        }),
      );
    } else if (result >= 3) {
      setNewPasswordError('');
    } else {
      setNewPasswordError(weakPassword);
    }
  };
  const onConfirmedPasswordChangeHandler = (value: string) => {
    if (confirmedPassword.length === 0 && value.length > 0) {
      setConfirmedPasswordError('');
    }

    setConfirmedPassword(value);
  };

  const onSubmitHandler = async () => {
    setConfirmedPasswordError('');

    if (!currentPassword || !newPassword || !confirmedPassword) {
      if (!currentPassword) {
        setCurrentPasswordError(requiredField);
      }
      if (!confirmedPassword) {
        setConfirmedPasswordError(requiredField);
      }
      setNewPasswordError('');
      return;
    }

    if (score < 3) {
      setNewPasswordError(weakPassword);
      return;
    }

    if (newPassword !== confirmedPassword) {
      setNewPasswordError('');
      setConfirmedPasswordError(mustMatch);
      return;
    }

    try {
      setIsUserUpdating(true);
      const isSuccess = await dispatch(
        updateUser({
          old_password: currentPassword,
          new_password: newPassword,
        }),
      );

      await refreshUser();
      setIsUserUpdating(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmedPassword('');

      if (isSuccess) {
        showToast(EToastVariant.Success, successToastTitle, successToastDescription);
      } else {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({
            defaultMessage: 'Error',
            id: 'KN7zKn',
          }),
          intl.formatMessage({
            defaultMessage: 'Opps, there was a error. Please try again.',
            id: 'NDvJu/',
          }),
        );
      }
    } catch (error) {
      await refreshUser();
      setIsUserUpdating(false);
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        error.error
          ? error.error
          : intl.formatMessage({
              defaultMessage: 'Oops, something went wrong. Try again later.',
              id: '/m4g4K',
            }),
      );
    }
  };

  return (
    <div id="passwordChangeZone" className="md:pb-8">
      <Heading variant={EHeadingStyleVariant.H6} className="mb-6">
        <FormattedMessage defaultMessage="Password" id="5sg7KC" />
      </Heading>
      <TextInput
        controlled
        inputValue={currentPassword}
        onChange={onCurrentPasswordChangeHandler}
        ariaLabel={currentPasswordLabel}
        label={currentPasswordLabel}
        className="mt-3"
        type={ETextInputTypes.Password}
        showPasswordButtonAriaLabel={showPasswordLabel}
        error={currentPasswordError !== ''}
        helpText={currentPasswordError}
        disabled={isUserUpdating}
      />
      <TextInput
        controlled
        inputValue={newPassword}
        onChange={onNewPasswordChangeHandler}
        ariaLabel={newPasswordLabel}
        label={newPasswordLabel}
        className="mt-3"
        type={ETextInputTypes.Password}
        showPasswordButtonAriaLabel={showPasswordLabel}
        error={
          newPasswordError !== '' ||
          (confirmedPasswordError !== '' && confirmedPasswordError != requiredField)
        }
        helpText={newPasswordError}
        disabled={isUserUpdating}
      />
      <TextInput
        controlled
        inputValue={confirmedPassword}
        onChange={onConfirmedPasswordChangeHandler}
        ariaLabel={confirmNewPasswordLabel}
        label={confirmNewPasswordLabel}
        className="mt-3"
        type={ETextInputTypes.Password}
        showPasswordButtonAriaLabel={showPasswordLabel}
        error={confirmedPasswordError !== ''}
        helpText={confirmedPasswordError}
        disabled={isUserUpdating}
      />
      {newPassword && (
        <div className="flex justify-end mt-3">
          <Button
            label={changePasswordLabel}
            onClick={onSubmitHandler}
            loading={isUserUpdating}
            disabled={isUserUpdating || newPasswordError !== ''}
            fullWidth={!isGreaterThanTablet}
          />
        </div>
      )}
    </div>
  );
};
